// Role presets (docs/decisions/role-presets.md): helpers shared by the admin
// presets section, the users table and the user drawer.
import { COLUMN_LAYOUT_KEY, normalizeColumnLayout } from './inventoryColumns';

export const PRESET_NAME_MAX = 40;
export const PRESET_DESCRIPTION_MAX = 200;
export const PRESET_CONFLICT_MESSAGE = 'Someone else changed this preset. Reload to see their version.';

/** A preset's column layout (normalised; the default layout when missing). */
export const presetLayout = (preset) => normalizeColumnLayout(preset?.values?.[COLUMN_LAYOUT_KEY]);

/**
 * The id of the preset a member's layout starts from: their own pick when
 * set (`preset_choice_id`, else matched by `preset_choice_name`, names are
 * unique per dealership), else the admin's assignment (`preset_id`).
 */
export const choiceIdOf = (user, presets = []) => {
    if (user?.preset_choice_id) return user.preset_choice_id;
    const name = user?.preset_choice_name;
    if (!name) return null;
    const hit = presets.find((p) => String(p.name).toLowerCase() === String(name).toLowerCase());
    return hit ? hit.id : null;
};

export const effectivePresetId = (user, presets = []) => choiceIdOf(user, presets) || user?.preset_id || null;

/** "assigned: Sales Manager · user chose: CEO" parts for one member. */
export const presetSummary = (user) => {
    const assigned = user?.preset_name || null;
    const chose = user?.preset_choice_name || null;
    return {
        assigned,
        chose: chose && chose !== assigned ? chose : null,
    };
};

const count = (n, one, many) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

/**
 * Who a delete touches (fresh QA F-3), counted like the server clears them:
 * `assigned` = members whose admin assignment is this preset (the
 * assignment is removed; they start from their own pick, else the system
 * default); `picked` = users whose own pick is this preset (cleared; they
 * fall back to their admin's assignment or the system default), including
 * users outside the dealership (`outside`, e.g. vendor staff): the
 * preset's `assigned` count holds them, the member list does not.
 */
export const deleteImpact = (preset, members = [], presets = []) => {
    if (!preset) return { assigned: 0, picked: 0, outside: 0 };
    const starting = members.filter((u) => effectivePresetId(u, presets) === preset.id).length;
    const outside = Math.max(0, (Number(preset.assigned) || 0) - starting);
    return {
        assigned: members.filter((u) => u.preset_id === preset.id).length,
        picked: members.filter((u) => choiceIdOf(u, presets) === preset.id).length + outside,
        outside,
    };
};

/** The delete confirm's sentences after "Delete X?" (fresh QA F-3). */
export const deleteConfirmText = ({ assigned = 0, picked = 0 } = {}) => {
    if (!assigned && !picked) return 'Nobody starts from it.';
    const parts = [];
    if (assigned) {
        parts.push(`${count(assigned, 'user is', 'users are')} assigned to it: the ${assigned === 1 ? 'assignment is' : 'assignments are'} `
            + 'removed and they start from their own pick if they made one, otherwise from the system default.');
    }
    if (picked) {
        parts.push(`${count(picked, 'user', 'users')} picked it as their own starting layout: they fall back to their admin's `
            + 'assignment or the system default.');
    }
    parts.push('Their own column changes stay. The history keeps the record.');
    return parts.join(' ');
};

/** The toast after a delete, from the server's cleared counts (fresh QA F-3). */
export const deleteToastText = (name, res = {}) => {
    const a = Number(res?.cleared_assignments) || 0;
    const c = Number(res?.cleared_choices) || 0;
    const parts = [`${name} deleted${a || c ? '.' : ''}`];
    if (a) {
        parts.push(a === 1
            ? '1 assignment removed: that user starts from their own pick or the system default.'
            : `${count(a, 'assignment', 'assignments')} removed: those users start from their own pick or the system default.`);
    }
    if (c) {
        parts.push(`${count(c, 'user who picked it falls', 'users who picked it fall')} back to their admin's assignment or the system default.`);
    }
    return parts.join(' ');
};

/** Per-field messages for a preset draft (empty object = valid). */
export const validatePresetDraft = (draft, others = []) => {
    const errors = {};
    const name = String(draft?.name || '').trim();
    if (!name) errors.name = 'Enter a name.';
    else if (name.length > PRESET_NAME_MAX) errors.name = `Use at most ${PRESET_NAME_MAX} characters.`; // the server's wording (fresh QA F-8)
    else if (others.some((p) => String(p.name).trim().toLowerCase() === name.toLowerCase())) {
        errors.name = 'Another preset already has this name.';
    }
    if (String(draft?.description || '').length > PRESET_DESCRIPTION_MAX) {
        errors.description = `Use at most ${PRESET_DESCRIPTION_MAX} characters.`;
    }
    return errors;
};

/** A free name for a copy: "CEO (copy)", "CEO (copy 2)", … within the length limit. */
export const copyName = (name, presets = []) => {
    const taken = new Set(presets.map((p) => String(p.name).toLowerCase()));
    for (let i = 1; i < 100; i += 1) {
        const suffix = i === 1 ? ' (copy)' : ` (copy ${i})`;
        const candidate = `${String(name).slice(0, PRESET_NAME_MAX - suffix.length)}${suffix}`;
        if (!taken.has(candidate.toLowerCase())) return candidate;
    }
    return String(name).slice(0, PRESET_NAME_MAX);
};

/** The server's text for a rejected write, if it sent one. */
export const apiMessage = (err, fallback) => {
    const detail = err?.details?.detail ?? err?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object' && detail.errors && typeof detail.errors === 'object') {
        const msgs = Object.values(detail.errors).flat().filter((m) => typeof m === 'string');
        if (msgs.length) return msgs.join(' ');
    }
    if (detail && typeof detail === 'object' && typeof detail.message === 'string') return detail.message;
    if (Array.isArray(detail)) {
        const text = detail.map((d) => d?.msg || d?.message).filter(Boolean).join(' ');
        if (text) return text;
    }
    return fallback;
};

/** A 409 that is about the name (duplicate), not the version. */
export const isNameConflict = (err) => {
    if (err?.status !== 409) return false;
    const detail = err?.details?.detail ?? err?.response?.data?.detail;
    if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
        if (detail.field) return detail.field === 'name';
        if (detail.current_version !== undefined) return false;
    }
    const text = apiMessage(err, '');
    return /name/i.test(text) && !/version/i.test(text);
};

const ACTION_LABEL = Object.freeze({
    create: 'Created', update: 'Changed', delete: 'Deleted', restore: 'Restored',
});

/** One history row as { action, what } for the table. */
export const describeHistory = (row) => {
    const action = ACTION_LABEL[row?.action] || row?.action || '—';
    const before = row?.old || {};
    const after = row?.new || {};
    const parts = [];
    if (row?.action === 'update') {
        if (before.name !== undefined && after.name !== undefined && before.name !== after.name) parts.push(`Renamed "${before.name}" → "${after.name}"`);
        if ((before.description ?? '') !== (after.description ?? '') && after.description !== undefined) parts.push('Description changed');
        const a = JSON.stringify(before.values?.[COLUMN_LAYOUT_KEY] ?? null);
        const b = JSON.stringify(after.values?.[COLUMN_LAYOUT_KEY] ?? null);
        if (a !== b) parts.push('Column layout changed');
    } else {
        const name = after.name || before.name;
        if (name) parts.push(`"${name}"`);
    }
    return { action, what: parts.join(' · ') || '—' };
};
